import {makeAutoObservable, reaction, runInAction} from 'mobx'
import Agent from 'api/Agent'
import {IFirstContactAttachment, FirstContactAttachmentFormValues} from 'models/FirstContactAttachment/FirstContactAttachment'
import {toast} from 'react-toastify'
import {RootStore} from 'stores/RootStore'
import dayjs from 'dayjs'


export default class FirstContactAttachmentStore {
    rootStore: RootStore

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore
        makeAutoObservable(this)

        reaction(
            () => this.predicate.keys(),
            () => {
                this.page = 0
                this.firstContactAttachmentRegistry.clear()
                this.loadFirstContactAttachment()
            }
        )
        reaction(
            () => this.currentFirstContactId,
            () => {
                this.page = 0
                this.firstContactAttachmentRegistry.clear()
            }
        )
    }

    firstContactAttachmentRegistry = new Map()
    firstContactAttachment: IFirstContactAttachment | null = null
    editMode = false


    currentFirstContactId = '';
    firstContactAttachmentCount = 0
    page = 0
    limit = 5

    predicate = new Map()

    setCurrentFirstContactId = (firstContactId: string) => {
        this.currentFirstContactId = firstContactId;
    }

    setPredicate = (predicate?: string, value?: any) => {
        if (!!predicate) {
            if (predicate !== 'all') {
                this.predicate.delete(predicate)
                if (!!value) this.predicate.set(predicate, value instanceof Date ? new Date(value).toISOString() : value)
            } else {
                this.predicate.clear()
            }
        }
    }

    get axiosParams() {
        const params = new URLSearchParams()
        params.append('limit', String(this.limit))
        params.append('offset', `${this.page ? this.page * this.limit : 0}`)
        this.predicate.forEach((value, key) => {
            if (!!value) params.append(key, value)
        })
        return params
    }

    get totalPages() {
        return Math.ceil(this.firstContactAttachmentCount / this.limit)
    }

    get firstContactAttachmentsById() {
        return Array.from(this.firstContactAttachmentRegistry.values()).sort((a, b) => b.id - a.id)
    }

    get firstContactAttachmentsByDate() {
        return Array.from(this.firstContactAttachmentRegistry.values()).sort(
            (a, b) => dayjs(b.createdOn).isBefore(a.createdOn) ? -1 : 1//   Date.parse(a.SubmittedOn)-Date.parse(b.SubmittedOn)
        )
    }

    getFirstContactAttachment = (id: string) => {
        return this.firstContactAttachmentRegistry.get(id)
    }

    setPage = (page: number) => {
        this.page = page
    }

    setLimit = (limit: number) => {
        this.limit = limit
    }

    handleChangePage = (newPage: number, newRow: number) => {
        //sets page, we do -1 because the offset will be multiplied against 
        this.setPage(newPage - 1)
        //sets the row limit
        this.setLimit(newRow)
        //clears all records in the registry so that next page didnt load the prev records
        this.firstContactAttachmentRegistry.clear()
        this.loadFirstContactAttachment()
    }

    clearFirstContactAttachment = () => {
        this.firstContactAttachmentRegistry.clear()
    }

    loadFirstContactAttachment = async () => {
        let loadingId = this.rootStore.commonStore.setIsLoading()

        try {
            if (!!this.currentFirstContactId) {
                const firstContactAttachmentsEnvelope = await Agent.FirstContactAttachment.list(this.axiosParams);
                const {modelsDto, modelCount} = firstContactAttachmentsEnvelope;

                runInAction(() => {
                    this.clearFirstContactAttachment();
                    modelsDto.forEach((firstContactAttachment: IFirstContactAttachment) => {
                        this.firstContactAttachmentRegistry.set(firstContactAttachment.id, firstContactAttachment)
                    })
                    this.firstContactAttachmentCount = modelCount
                })
            }
        } catch (error) {
            throw error
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }

    getFirstContactAttachmentById = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            let firstContactAttachment = await Agent.FirstContactAttachment.byId(id)
            runInAction(() => {
                this.firstContactAttachment = firstContactAttachment
                this.firstContactAttachmentRegistry.set(firstContactAttachment.id, firstContactAttachment)
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            return firstContactAttachment
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }

    createFirstContactAttachment = async (firstContactAttachment: FirstContactAttachmentFormValues) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()

        try {
            const newAttachment = await Agent.FirstContactAttachment.create(firstContactAttachment)
            runInAction(() => {
                // this.firstContactAttachmentRegistry.set(newAttachment.id, newAttachment)
                this.rootStore.commonStore.setIsLoading(loadingId)
                this.rootStore.modalStore.closeModal('FirstContactAttachmentModal')
            })

            toast.success('Saved')
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    deleteFirstContactAttachment = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            await Agent.FirstContactAttachment.delete(id)
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
                this.rootStore.modalStore.closeModal('FirstContactAttachmentStore')
            })
            toast.success('Deleted!')
        } catch (error) {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            this.rootStore.errorStore.addError(error)
        }
    }

}
